import {CompositeLayer} from '@deck.gl/core';
import {IconLayer, TextLayer} from '@deck.gl/layers';
import Supercluster from 'supercluster';

const DEFAULT_COLOR = [0, 0, 0, 255];

const defaultProps = {
    iconAtlas: {type: 'object', value: null, async: true},
    iconMapping: {type: 'object', value: {}, async: true},
    sizeScale: {type: 'number', value: 1, min: 0},
    pickable: true,
    billboard: true,
    sizeUnits: 'pixels',
    sizeMinPixels: {type: 'number', min: 0, value: 0}, //  min point radius in pixels
    sizeMaxPixels: {type: 'number', min: 0, value: Number.MAX_SAFE_INTEGER}, // max point radius in pixels
    alphaCutoff: {type: 'number', value: 0.05, min: 0, max: 1},
    transitions: {type: 'object', value: {
        "getColors": {
            "duration": 300,
            "easing": t=>((t *= 2) <= 1 ? t * t : --t * (2 - t) + 1) / 2,
            "enter": value => [value[0], value[1], value[2], 0]
        }
    }},
    fontFamily: {type: 'string', value: 'Source Sans Pro, monospace'},

    getPosition: {type: 'accessor', value: x => x.position},
    getIcon: {type: 'accessor', value: x => x.icon},
    getColor: {type: 'accessor', value: DEFAULT_COLOR},
    getSize: {type: 'accessor', value: 1},
    getAngle: {type: 'accessor', value: 0},
    getPixelOffset: {type: 'accessor', value: [0, 32]},

    getTextAnchor: {type: 'accessor', value: 'middle'},
    getAlignmentBaseline: {type: 'accessor', value: 'center'}
};

function getIconName(size) {
    if (size === 0) {
        return '';
    }
    if (size < 10) {
        return `marker-${size}`;
    }
    if (size < 100) {
        return `marker-${Math.floor(size / 10)}0`;
    }
    return 'marker-100';
}

function getIconSize(size) {
    return Math.min(100, size) / 100 + 1;
}

export default class IconClusterLayer extends CompositeLayer {
    shouldUpdateState({changeFlags}) {
        return changeFlags.somethingChanged;
    }

    updateState({props, oldProps, changeFlags}) {
        const rebuildIndex = changeFlags.dataChanged || props.sizeScale !== oldProps.sizeScale;

        if (rebuildIndex) {
            const index = new Supercluster({maxZoom: 16, radius: props.sizeScale});
            index.load(
                props.data.map(d => ({
                    geometry: {coordinates: props.getPosition(d)},
                    properties: d
                }))
            );
            this.setState({index});
        }

        const z = Math.floor(this.context.viewport.zoom);
        if (rebuildIndex || z !== this.state.z) {
            this.setState({
                data: this.state.index.getClusters([-180, -85, 180, 85], z),
                z
            });
        }
    }

    getPickingInfo({info, mode}) {
        const pickedObject = info.object && info.object.properties;
        if (pickedObject) {
            if (pickedObject.cluster && mode !== 'hover') {
                info.objects = this.state.index
                    .getLeaves(pickedObject.cluster_id, 25)
                    .map(f => f.properties);
            }
            info.object = pickedObject;
        }
        return info;
    }

    renderLayers() {
        const {data} = this.state;
        const {
            iconAtlas,
            iconMapping,
            sizeScale,
            getIcon,
            getColor,
            getSize,
            getText,
            getPixelOffset,
            getTextAnchor,
            getAlignmentBaseline,
            transitions,
            fontFamily
        } = this.props;
        const iconLayer = new IconLayer(
            this.getSubLayerProps({
                id: 'icon',
                data,
                iconAtlas,
                iconMapping,
                sizeScale,
                getColor,
                getPosition: d => d.geometry.coordinates,
                getIcon,//getIconName(d.properties.cluster ? d.properties.point_count : 1),
                getSize: d => getIconSize(d.properties.cluster ? d.properties.point_count : getSize),
                transitions
            })
        );
        const textLayer = new TextLayer(
            this.getSubLayerProps({
                id: 'text',
                data,
                getText,
                getSize,
                getColor,
                getTextAnchor,
                getAlignmentBaseline,
                getPosition: d => d.geometry.coordinates,
                getPixelOffset,
                transitions,
                fontFamily
            })
        );
        return [iconLayer, textLayer];
    }
}

IconClusterLayer.layerName = 'IconClusterLayer';
IconClusterLayer.defaultProps = defaultProps;